import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, Edit, AlertTriangle, Download, FileUp, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import CustomerMergingPanel from "@/components/CustomerMergingPanel";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">Ayarlar</h1>
        <p className="text-xs text-slate-500 mt-1">Sistem ayarları, referans veriler ve denetim kaydı.</p>
      </div>
      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="bg-white border border-[#DCE2E8] shadow-sm p-1 flex flex-nowrap overflow-x-auto w-full justify-start scrollbar-thin">
          <TabsTrigger value="general" data-testid="settings-tab-general" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Genel</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="settings-tab-suppliers" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Tedarikçiler</TabsTrigger>
          <TabsTrigger value="products" data-testid="settings-tab-products" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Ürün Kategorileri</TabsTrigger>
          <TabsTrigger value="costs" data-testid="settings-tab-costs" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Maliyet Kategorileri</TabsTrigger>
          <TabsTrigger value="audit" data-testid="settings-tab-audit" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">İşlem Kayıtları</TabsTrigger>
          {isAdmin && <TabsTrigger value="customer-merging" data-testid="settings-tab-customer-merging" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Müşteri Birleştirme</TabsTrigger>}
          {isAdmin && <TabsTrigger value="backups" data-testid="settings-tab-backups" className="shrink-0 px-3 py-2 text-xs sm:text-sm font-medium">Veri Yedekleme</TabsTrigger>}
        </TabsList>
        <TabsContent value="general"><GeneralPanel /></TabsContent>
        <TabsContent value="suppliers"><SuppliersPanel /></TabsContent>
        <TabsContent value="products"><ProductCategoriesPanel /></TabsContent>
        <TabsContent value="costs"><SimpleList endpoint="/cost-categories" title="Maliyet Kategorileri" /></TabsContent>
        <TabsContent value="audit"><AuditPanel /></TabsContent>
        {isAdmin && <TabsContent value="customer-merging"><CustomerMergingPanel /></TabsContent>}
        {isAdmin && <TabsContent value="backups"><BackupsPanel /></TabsContent>}
      </Tabs>
    </div>
  );
}

function BackupsPanel() {
  const isMobile = useIsMobile();
  const [isRunning, setIsRunning] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [isInspecting, setIsInspecting] = useState(false);
  const [inspectData, setInspectData] = useState(null);

  const handleCreateBackup = async () => {
    if (isMobile) return;
    setIsRunning(true);
    try {
      setProgressSteps([
        { label: "Yedek hazırlanıyor", status: "active" },
        { label: "Veritabanı yedekleniyor", status: "pending" },
        { label: "Dosyalar paketleniyor", status: "pending" },
        { label: "Bütünlük bilgisi oluşturuluyor", status: "pending" },
        { label: "Yedek hazır", status: "pending" }
      ]);

      await new Promise((r) => setTimeout(r, 600));
      setProgressSteps((steps) => [
        { label: "Yedek hazırlanıyor", status: "success" },
        { label: "Veritabanı yedekleniyor", status: "active" },
        ...steps.slice(2)
      ]);

      await new Promise((r) => setTimeout(r, 800));
      setProgressSteps((steps) => [
        steps[0],
        { label: "Veritabanı yedekleniyor", status: "success" },
        { label: "Dosyalar paketleniyor", status: "active" },
        ...steps.slice(3)
      ]);

      await new Promise((r) => setTimeout(r, 800));
      setProgressSteps((steps) => [
        ...steps.slice(0, 2),
        { label: "Dosyalar paketleniyor", status: "success" },
        { label: "Bütünlük bilgisi oluşturuluyor", status: "active" },
        steps[4]
      ]);

      await new Promise((r) => setTimeout(r, 600));
      setProgressSteps((steps) => [
        ...steps.slice(0, 3),
        { label: "Bütünlük bilgisi oluşturuluyor", status: "success" },
        { label: "Yedek hazır", status: "active" }
      ]);

      const response = await api.post("/admin/backups", {}, { responseType: "blob" });

      setProgressSteps((steps) => [
        ...steps.slice(0, 4),
        { label: "Yedek hazır", status: "success" }
      ]);

      const blob = new Blob([response.data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers["content-disposition"];
      let filename = `GP_Yonetim_Backup_${new Date().toISOString().slice(0, 10)}.gpbak`;
      if (disposition && disposition.indexOf("filename=") !== -1) {
        filename = disposition.split("filename=")[1].replace(/["']/g, "");
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Yedek başarıyla oluşturuldu.");
    } catch (err) {
      toast.error("Yedek alınırken bir hata oluştu: " + formatApiError(err));
    } finally {
      setIsRunning(false);
    }
  };

  const handleInspectFileChange = async (e) => {
    if (isMobile) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setIsInspecting(true);
    setInspectData(null);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/admin/backups/inspect", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setInspectData(response.data);
      toast.success("Yedek dosyası doğrulandı.");
    } catch (err) {
      toast.error("Yedek dosyası incelenirken hata oluştu: " + formatApiError(err));
      setInspectData(null);
    } finally {
      setIsInspecting(false);
      e.target.value = "";
    }
  };

  const handleRestoreFileChange = async (e) => {
    if (isMobile) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const warnText = "UYARI\n\nMevcut veritabanı, yüklenen dosyalar ve uygulama ayarları bu yedekteki içerikle değiştirilecektir.\n\nBu işlem tamamlandıktan sonra mevcut verilerin önceki hali doğrudan geri alınamaz.";
    if (!window.confirm(`${warnText}\n\nDevam etmek istediğinize emin misiniz?`)) {
      e.target.value = "";
      return;
    }

    const confirmText = window.prompt("Lütfen geri yükleme işlemini onaylamak için büyük harflerle 'GERİ YÜKLE' yazın:");
    if (confirmText !== "GERİ YÜKLE") {
      toast.error("İşlem onaylanmadı.");
      e.target.value = "";
      return;
    }

    setIsRunning(true);
    try {
      setProgressSteps([
        { label: "Yedek doğrulanıyor", status: "active" },
        { label: "Güvenlik yedeği oluşturuluyor", status: "pending" },
        { label: "Veritabanı geri yükleniyor", status: "pending" },
        { label: "Dosyalar geri yükleniyor", status: "pending" },
        { label: "Sistem doğrulanıyor", status: "pending" },
        { label: "Geri yükleme tamamlandı", status: "pending" }
      ]);

      let currentStep = 0;
      const interval = setInterval(() => {
        if (currentStep < 4) {
          setProgressSteps((steps) => {
            const updated = [...steps];
            updated[currentStep].status = "success";
            updated[currentStep + 1].status = "active";
            return updated;
          });
          currentStep++;
        }
      }, 1200);

      const formData = new FormData();
      formData.append("file", file);

      await api.post("/admin/backups/restore", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      clearInterval(interval);

      setProgressSteps([
        { label: "Yedek doğrulanıyor", status: "success" },
        { label: "Güvenlik yedeği oluşturuluyor", status: "success" },
        { label: "Veritabanı geri yükleniyor", status: "success" },
        { label: "Dosyalar geri yükleniyor", status: "success" },
        { label: "Sistem doğrulanıyor", status: "success" },
        { label: "Geri yükleme tamamlandı", status: "success" }
      ]);

      toast.success("Veriler başarıyla geri yüklendi.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);

    } catch (err) {
      toast.error("Geri yükleme başarısız oldu: " + formatApiError(err));
    } finally {
      setIsRunning(false);
      e.target.value = "";
    }
  };

  if (isMobile) {
    return (
      <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 shadow-sm text-sm text-slate-600">
        Yedekleme ve geri yükleme işlemleri yalnızca masaüstü görünümde gerçekleştirilebilir.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="backups-panel">
      <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-6 shadow-sm">
        <div>
          <h3 className="text-lg font-bold text-[#171A1F]">Sistem Yedekleme ve Kurtarma</h3>
          <p className="text-xs text-slate-500 mt-1">
            GP Yönetim uygulamasının tam yedekleme ve felaket kurtarma aracı. Veritabanı ve tüm yüklenen dosyaları kapsar.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {/* Action 1: Yedek Al */}
          <div className="rounded-lg border border-[#DCE2E8] bg-[#F4F6F8] p-4 flex flex-col justify-between space-y-3">
            <div>
              <h4 className="font-bold text-[#171A1F] text-sm">Yedek Al</h4>
              <p className="text-xs text-slate-500 mt-1">
                Tüm veritabanı, ekleri ve uygulama ayarlarını içeren tek bir indirilebilir .gpbak dosyası oluşturun.
              </p>
            </div>
            <Button
              onClick={handleCreateBackup}
              disabled={isRunning || isInspecting}
              className="w-full bg-[#FF353A] hover:bg-[#E02B30] text-white flex items-center justify-center text-xs font-medium shadow-sm"
              data-testid="btn-create-backup"
            >
              {isRunning ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Download className="mr-1.5" size={14} />}
              Yedek Al
            </Button>
          </div>

          {/* Action 2: Yedek Bilgisini Görüntüle */}
          <div className="rounded-lg border border-[#DCE2E8] bg-[#F4F6F8] p-4 flex flex-col justify-between space-y-3">
            <div>
              <h4 className="font-bold text-[#171A1F] text-sm">Yedek Bilgisini Görüntüle</h4>
              <p className="text-xs text-slate-500 mt-1">
                Bir yedek dosyasını geri yüklemeden önce güvenle inceleyin ve bütünlük/uyumluluk durumunu kontrol edin.
              </p>
            </div>
            <div className="relative">
              <input
                type="file"
                accept=".gpbak"
                onChange={handleInspectFileChange}
                disabled={isRunning || isInspecting}
                className="hidden"
                id="inspect-file-input"
                data-testid="inspect-file-input"
              />
              <label htmlFor="inspect-file-input" className="w-full">
                <Button
                  asChild
                  disabled={isRunning || isInspecting}
                  className="w-full bg-white border border-[#DCE2E8] text-[#171A1F] hover:bg-slate-50 cursor-pointer flex items-center justify-center text-xs font-medium shadow-sm"
                  data-testid="btn-inspect-backup"
                >
                  <span>
                    {isInspecting ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <FileUp className="mr-1.5" size={14} />}
                    Yedek Bilgisini Görüntüle
                  </span>
                </Button>
              </label>
            </div>
          </div>

          {/* Action 3: Yedekten Geri Yükle */}
          <div className="rounded-lg border border-[#DCE2E8] bg-[#F4F6F8] p-4 flex flex-col justify-between space-y-3">
            <div>
              <h4 className="font-bold text-[#171A1F] text-sm">Yedekten Geri Yükle</h4>
              <p className="text-xs text-slate-500 mt-1">
                Seçeceğiniz bir yedek dosyasını sisteme yükleyerek tüm uygulama durumunu ve dosyaları felaketten kurtarın.
              </p>
            </div>
            <div className="relative">
              <input
                type="file"
                accept=".gpbak"
                onChange={handleRestoreFileChange}
                disabled={isRunning || isInspecting}
                className="hidden"
                id="restore-file-input"
                data-testid="restore-file-input"
              />
              <label htmlFor="restore-file-input" className="w-full">
                <Button
                  asChild
                  disabled={isRunning || isInspecting}
                  className="w-full bg-rose-600 hover:bg-rose-500 text-white cursor-pointer flex items-center justify-center text-xs font-medium shadow-sm"
                  data-testid="btn-restore-backup"
                >
                  <span>
                    {isRunning ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <AlertTriangle className="mr-1.5" size={14} />}
                    Yedekten Geri Yükle
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </div>

        {/* Progress Display */}
        {isRunning && progressSteps.length > 0 && (
          <div className="rounded-lg border border-[#DCE2E8] p-4 bg-[#F4F6F8] space-y-3" data-testid="backup-progress-card">
            <h4 className="font-semibold text-[#171A1F] text-xs uppercase tracking-wider">İşlem Durumu</h4>
            <div className="space-y-2">
              {progressSteps.map((step, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  {step.status === "success" ? (
                    <span className="text-emerald-600 font-bold">✓</span>
                  ) : step.status === "active" ? (
                    <Loader2 className="animate-spin text-[#FF353A]" size={12} />
                  ) : (
                    <span className="text-slate-300">•</span>
                  )}
                  <span className={step.status === "active" ? "font-bold text-[#171A1F]" : step.status === "success" ? "text-slate-500" : "text-slate-400"}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inspect Details */}
        {inspectData && (
          <div className="rounded-lg border border-[#DCE2E8] p-5 bg-white space-y-4 shadow-sm" data-testid="inspect-details-card">
            <div className="flex items-center gap-2 text-[#171A1F] border-b border-[#DCE2E8] pb-3">
              <ShieldCheck className="text-emerald-600" size={18} />
              <h4 className="font-bold text-sm">Yedek Dosyası Analiz Raporu</h4>
            </div>

            <div className="grid gap-x-6 gap-y-3 grid-cols-2 text-xs">
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Yedek Tarihi:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.created_at ? new Date(inspectData.created_at).toLocaleString("tr-TR") : "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Uygulama Sürümü:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.app_version || "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Yedek Formatı:</span>
                <span className="font-medium text-[#171A1F]">Sürüm {inspectData.backup_format_version || "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Veritabanı Sürümü:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.alembic_version || "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Toplam Boyut:</span>
                <span className="font-medium text-[#171A1F]">
                  {inspectData.total_backup_size_bytes ? `${(inspectData.total_backup_size_bytes / 1024 / 1024).toFixed(2)} MB` : "-"}
                </span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">İş Sayısı:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.job_count ?? "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Sipariş Sayısı:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.order_count ?? "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Maliyet Kaydı Sayısı:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.cost_count ?? "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Kullanıcı Sayısı:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.user_count ?? "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Tedarikçi Sayısı:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.supplier_count ?? "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Dosya Sayısı:</span>
                <span className="font-medium text-[#171A1F]">{inspectData.uploaded_file_count ?? "-"}</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">Dosyaların Toplam Boyutu:</span>
                <span className="font-medium text-[#171A1F]">
                  {inspectData.uploaded_file_total_size_bytes ? `${(inspectData.uploaded_file_total_size_bytes / 1024).toFixed(1)} KB` : "0 KB"}
                </span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5 col-span-2">
                <span className="text-slate-500">Bütünlük Kontrolü:</span>
                <span className="font-semibold text-emerald-600">Başarılı</span>
              </div>
              <div className="flex justify-between border-b border-slate-100 pb-1.5 col-span-2">
                <span className="text-slate-500">Geri Yükleme Uyumluluğu:</span>
                <span className="font-semibold text-emerald-600">Uyumlu</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function GeneralPanel() {
  const isMobile = useIsMobile();
  const { refreshSettings } = useAuth();
  const [minMargin, setMinMargin] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setMinMargin(String(r.data.min_margin_percent)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (isMobile) return;
    const n = parseFloat(String(minMargin).replace(",", "."));
    if (Number.isNaN(n) || n < 0 || n > 100) {
      toast.error("Minimum marj 0 ile 100 arasında olmalıdır.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings", { min_margin_percent: String(n) });
      toast.success("Ayarlar güncellendi.");
      refreshSettings?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>;
  return (
    <div className="rounded-lg border border-[#DCE2E8] bg-white p-6 space-y-4 max-w-2xl shadow-sm">
      <div>
        <h3 className="text-lg font-bold text-[#171A1F]">Kâr / Marj Uyarı Eşiği</h3>
        <p className="text-xs text-slate-500 mt-1">
          Bu değerin altında kalan pozitif marjlar iş listelerinde ve maliyet ekranlarında
          <span className="mx-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">Düşük Marj</span>
          etiketiyle sarı olarak gösterilir.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-slate-700 text-xs font-semibold uppercase tracking-wider">Minimum Brüt Kâr Marjı (%)</Label>
          <Input
            inputMode="decimal"
            value={minMargin}
            onChange={(e) => setMinMargin(e.target.value.replace(",", "."))}
            disabled={isMobile}
            readOnly={isMobile}
            className="border-[#DCE2E8] font-mono"
            data-testid="min-margin-input"
          />
        </div>
        {!isMobile && (
          <div className="flex items-end">
            <Button onClick={save} disabled={saving} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium" data-testid="min-margin-save">
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save size={14} className="mr-2" />}
              Kaydet
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCategoriesPanel() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/product-categories");
      setItems(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAddCategory = async () => {
    if (isMobile) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Lütfen bir ürün kategorisi adı girin.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/product-categories", { name: trimmed });
      toast.success("Ürün kategorisi eklendi.");
      setName("");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (isMobile) return;
    if (!window.confirm("Bu ürün kategorisini KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/product-categories/${item.id}`);
      toast.success("Kategori kalıcı olarak silindi.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4">
      {!isMobile && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) setName("");
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium"
                data-testid="product-category-create-button"
              >
                <Plus size={14} className="mr-1" /> Ürün Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#DCE2E8]">
              <DialogHeader>
                <DialogTitle className="text-[#171A1F]">Yeni Ürün Kategorisi</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3 py-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Kategori / Ürün Adı</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Örn: Otomatik Kapı, Alüminyum Doğrama..."
                    className="border-[#DCE2E8]"
                    data-testid="product-category-name-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCategory();
                      }
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleAddCategory}
                  disabled={saving}
                  className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium"
                  data-testid="product-category-submit"
                >
                  {saving ? <Loader2 className="animate-spin mr-1.5" size={14} /> : null}
                  Ekle
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>
        ) : items.length === 0 ? (
          <EmptyState title="Ürün kategorisi yok" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ad</TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sıra</TableHead>
                {!isMobile && <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">İşlem</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="border-b border-[#DCE2E8]">
                  <TableCell className="font-medium text-[#171A1F]">{c.name}</TableCell>
                  <TableCell>{c.is_active ? <StatusBadge tone="success">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>}</TableCell>
                  <TableCell className="text-slate-600 font-mono">{c.sort_order}</TableCell>
                  {!isMobile && (
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(c)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`category-delete-${c.name}`}
                      >
                        <Trash2 size={14} className="mr-1" /> Sil
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function SimpleList({ endpoint, title }) {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(endpoint).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [endpoint]);

  const handleDelete = async (item) => {
    if (isMobile) return;
    const itemLabel = title === "Ürün Kategorileri" ? "ürün kategorisini" : "maliyet kategorisini";
    if (!window.confirm(`Bu ${itemLabel} KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success("Kategori kalıcı olarak silindi.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>;
  return (
    <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ad</TableHead>
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sıra</TableHead>
            {!isMobile && <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">İşlem</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id} className="border-b border-[#DCE2E8]">
              <TableCell className="font-medium text-[#171A1F]">{c.name}</TableCell>
              <TableCell>{c.is_active ? <StatusBadge tone="success">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>}</TableCell>
              <TableCell className="text-slate-600 font-mono">{c.sort_order}</TableCell>
              {!isMobile && (
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(c)}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    data-testid={`category-delete-${c.name}`}
                  >
                    <Trash2 size={14} className="mr-1" /> Sil
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SuppliersPanel() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/suppliers");
      setItems(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleEditSupplier = (s) => {
    if (isMobile) return;
    setEditingSupplier(s);
    setForm({
      name: s.name || "",
      contact_person: s.contact_person || "",
      phone: s.phone || "",
      email: s.email || "",
      notes: s.notes || "",
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (isMobile) return;
    if (!form.name) { toast.error("Ad zorunludur."); return; }
    setSaving(true);
    try {
      if (editingSupplier) {
        await api.patch(`/suppliers/${editingSupplier.id}`, form);
        toast.success("Tedarikçi güncellendi.");
      } else {
        await api.post("/suppliers", form);
        toast.success("Tedarikçi eklendi.");
      }
      setForm({ name: "", contact_person: "", phone: "", email: "", notes: "" });
      setEditingSupplier(null);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const handleDeleteSupplier = async (s) => {
    if (isMobile) return;
    if (!window.confirm("Bu tedarikçiyi KALICI OLARAK silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) return;
    const confirmText = window.prompt("Lütfen silme işlemini onaylamak için büyük harflerle 'SİL' yazın:");
    if (confirmText !== "SİL") {
      toast.error("Silme işlemi onaylanmadı.");
      return;
    }
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Tedarikçi kalıcı olarak silindi.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4">
      {!isMobile && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              setEditingSupplier(null);
              setForm({ name: "", contact_person: "", phone: "", email: "", notes: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium"
                data-testid="supplier-create-button"
                onClick={() => {
                  setEditingSupplier(null);
                  setForm({ name: "", contact_person: "", phone: "", email: "", notes: "" });
                }}
              >
                <Plus size={14} className="mr-1" /> Yeni Tedarikçi
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#DCE2E8]">
              <DialogHeader><DialogTitle className="text-[#171A1F]">{editingSupplier ? "Tedarikçi Düzenle" : "Yeni Tedarikçi"}</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label className="text-xs font-semibold text-slate-700">Ad</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="border-[#DCE2E8]" data-testid="supplier-name-input" /></div>
                <div><Label className="text-xs font-semibold text-slate-700">İletişim Kişisi</Label><Input value={form.contact_person} onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))} className="border-[#DCE2E8]" /></div>
                <div><Label className="text-xs font-semibold text-slate-700">Telefon</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="border-[#DCE2E8]" /></div>
                <div><Label className="text-xs font-semibold text-slate-700">E-posta</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="border-[#DCE2E8]" /></div>
                <div><Label className="text-xs font-semibold text-slate-700">Notlar</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="border-[#DCE2E8]" /></div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium"
                  data-testid="supplier-submit"
                >
                  {editingSupplier ? "Kaydet" : "Ekle"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        {loading ? <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div> :
         items.length === 0 ? <EmptyState title="Tedarikçi yok" /> :
         <Table>
          <TableHeader><TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ad</TableHead><TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İletişim</TableHead><TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Telefon</TableHead><TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">E-posta</TableHead>{!isMobile && <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">İşlem</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} className="border-b border-[#DCE2E8]">
                <TableCell className="font-medium text-[#171A1F]">{s.name}</TableCell>
                <TableCell className="text-slate-700">{s.contact_person || "-"}</TableCell>
                <TableCell className="text-slate-700">{s.phone || "-"}</TableCell>
                <TableCell className="text-slate-700">{s.email || "-"}</TableCell>
                {!isMobile && (
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditSupplier(s)}
                      className="text-slate-600 hover:text-slate-700 hover:bg-slate-50 mr-2"
                      data-testid={`supplier-edit-${s.name}`}
                    >
                      <Edit size={14} className="mr-1" /> Düzenle
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteSupplier(s)}
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      data-testid={`supplier-delete-${s.name}`}
                    >
                      <Trash2 size={14} className="mr-1" /> Sil
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
         </Table>}
      </div>
    </div>
  );
}

function AuditPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/audit", { params: { page, page_size: pageSize } })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [page, pageSize]);

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>;
  return (
    <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
      {items.length === 0 ? <EmptyState title="Kayıt yok" /> :
       <>
        <Table>
          <TableHeader><TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
            <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tarih</TableHead><TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Kullanıcı</TableHead><TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Modül</TableHead><TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İşlem</TableHead><TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Varlık</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id} className="border-b border-[#DCE2E8]" data-testid={`audit-row-${a.id}`}>
                <TableCell className="text-xs text-slate-600">{formatDateTime(a.created_at)}</TableCell>
                <TableCell className="text-sm font-medium text-[#171A1F]">{a.user_name || "-"}</TableCell>
                <TableCell className="text-sm text-slate-700">{a.module}</TableCell>
                <TableCell className="text-sm font-mono font-semibold text-[#FF353A]">{a.action}</TableCell>
                <TableCell className="text-xs text-slate-500">{a.entity_type || "-"} · {a.entity_id ? a.entity_id.slice(0, 8) : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />
       </>}
    </div>
  );
}
